import { useCallback, useState } from "react";
import useHttp from "./useHttp";

export default function useAI() {
  const { fetchData } = useHttp();
  const [loading, setLoading] = useState(false);

  const generateContent = useCallback(
    async (prompt, integId, appProperties, options = {}) => {
      const { model = 2, tone = null } = options;

      if (!integId) {
        throw new Error("Integration ID is required for AI content generation");
      }

      let finalPrompt = prompt;
      if (tone) {
        finalPrompt = `${prompt} Generate in a ${tone.toLowerCase()} tone.`;
      }

      const payload = { prompt: finalPrompt };

      setLoading(true);
      try {
        const response = await fetchData(`ai/${integId}/content/generate?model=${model}`, "POST", payload, appProperties);

        if (!response) {
          return {
            success: false,
            error: "Request was not completed",
          };
        }

        const axiosWrapper = JSON.parse(response);
        const body = axiosWrapper?.data;
        const apiStatus = body?.status;
        const rawData = body?.data;
        const generatedContent = typeof rawData === "string" ? rawData.trim() : "";

        const apiSaidSuccess = apiStatus === "true" || apiStatus === true;
        if (!apiSaidSuccess || generatedContent.length === 0) {
          const errorDetail =
            (typeof rawData === "string" && rawData.trim()) ||
            body?.license?.message ||
            "AI could not generate content. Please try again or send your original message.";
          return {
            success: false,
            error: errorDetail,
            data: generatedContent.length > 0 ? generatedContent : null,
          };
        }

        return {
          success: true,
          data: generatedContent,
          response: generatedContent,
        };
      } catch (error) {
        console.error("Error generating AI content:", error);
        return {
          success: false,
          error: error.message || "Failed to generate content",
        };
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  const uploadDocumentToKnowledgeBase = useCallback(
    async (formData, appProperties) => {
      const response = await fetchData("ai/knowledge-base/upload", "POST", formData, appProperties, {
        "Content-Type": "multipart/form-data",
      });
      const responseData = JSON.parse(response);
      return responseData?.data;
    },
    [fetchData]
  );

  const getAllKnowledgeSources = useCallback(
    async (appProperties) => {
      const response = await fetchData("ai/knowledge-base", "GET", null, appProperties);
      const responseData = JSON.parse(response);
      return responseData?.data;
    },
    [fetchData]
  );

  const deleteKnowledgeSource = useCallback(
    async (knowledgeSourceId, appProperties) => {
      const response = await fetchData(`ai/knowledge-base/${knowledgeSourceId}`, "DELETE", null, appProperties);
      const responseData = JSON.parse(response);
      return responseData?.data;
    },
    [fetchData]
  );

  const getSuggestedRepliesForContact = useCallback(
    async (contactId, appProperties, signal) => {
      const response = await fetchData(`ai/chat/${contactId}/reply-suggestion`, "POST", null, appProperties, { "Content-Type": "application/json" }, null, signal);
      const responseData = JSON.parse(response);
      return responseData?.data;
    },
    [fetchData]
  );

  const activateOrDeactivateAutoSuggestion = useCallback(
    async (payload, appProperties) => {
      const response = await fetchData("ai/features/auto_suggestion", "PUT", payload, appProperties);
      const responseData = JSON.parse(response);
      return responseData?.data;
    },
    [fetchData]
  );

  const activateOrDeactivateAutoReply = useCallback(
    async (payload, appProperties) => {
      const response = await fetchData("ai/features/auto_reply", "PUT", payload, appProperties);
      const responseData = JSON.parse(response);
      return responseData?.data;
    },
    [fetchData]
  );

    return {
        generateContent,
        uploadDocumentToKnowledgeBase,
        getAllKnowledgeSources,
        loading,
        deleteKnowledgeSource,
        getSuggestedRepliesForContact,
        activateOrDeactivateAutoSuggestion,
        activateOrDeactivateAutoReply,
    };
}
